import { FormGroup } from '@angular/forms';
import {
  FormRule,
  FormRuleEffect,
  FormRuleFactory,
  RuleAction,
} from 'src/app/shared/forms';
import { SubmissionConstants } from '../../models/constants/submissions-constants';

export class ManufactureDateRules {
  constructor(
    private ruleFact: FormRuleFactory,
    private submissionsConst: SubmissionConstants,
  ) {}

  getCustomRules(subValues: FormGroup): FormRule[] {
    const status = subValues.get('status')?.value;
    return [
      ...this.getSoldDateRules(subValues, status),
      ...this.getCompletionDateRules(subValues, status),
    ];
  }

  private getSoldDateRules(subValues: FormGroup, status: string): FormRule[] {
    const homeSoldDate = this.toDate(subValues.get('homeSoldDate')?.value);
    const homeManufacturedDate = this.toDate(
      subValues.get('homeManufacturedDate')?.value,
    );
    const createHomeSoldDateEffect = (action: RuleAction, value?: string) =>
      this.createBaseEffect(action, 'homeSoldDate', value);

    const shouldMakeSoldDateRequired =
      !homeSoldDate &&
      (status === this.submissionsConst.statusReadyToSubmit ||
        status === this.submissionsConst.statusSubmittedManually);
    const shouldShowSoldDateWarning =
      homeSoldDate && homeSoldDate < homeManufacturedDate;

    return [
      this.ruleFact.createRule(`${shouldMakeSoldDateRequired}`, [
        createHomeSoldDateEffect(RuleAction.MAKE_INVALID),
      ]),
      this.ruleFact.createRule(`${!shouldMakeSoldDateRequired}`, [
        createHomeSoldDateEffect(RuleAction.CLEAR_ERROR_MESSAGE),
      ]),
      this.ruleFact.createRule(`${shouldShowSoldDateWarning}`, [
        createHomeSoldDateEffect(RuleAction.MAKE_INVALID),
        createHomeSoldDateEffect(
          RuleAction.SET_ERROR_MESSAGE,
          this.submissionsConst.soldDateWarning,
        ),
      ]),
    ];
  }

  private getCompletionDateRules(
    subValues: FormGroup,
    status: string,
  ): FormRule[] {
    const completionDate = this.toDate(subValues.get('completionDate')?.value);
    const homeManufacturedDate = this.toDate(
      subValues.get('homeManufacturedDate')?.value,
    );
    const createCompletionDateEffect = (action: RuleAction, value?: string) =>
      this.createBaseEffect(action, 'completionDate', value);

    const shouldShowRequiredCompletionDate =
      !completionDate &&
      (status === this.submissionsConst.statusReadyToSubmit ||
        status === this.submissionsConst.statusSubmittedManually);

    const shouldShowCompletionDateWarning =
      completionDate && completionDate < homeManufacturedDate;

    return [
      this.ruleFact.createRule(`${shouldShowRequiredCompletionDate}`, [
        createCompletionDateEffect(RuleAction.MAKE_INVALID),
      ]),

      this.ruleFact.createRule(`${!shouldShowRequiredCompletionDate}`, [
        createCompletionDateEffect(RuleAction.CLEAR_ERROR_MESSAGE),
      ]),
      this.ruleFact.createRule(`${shouldShowCompletionDateWarning}`, [
        createCompletionDateEffect(RuleAction.MAKE_INVALID),
        createCompletionDateEffect(
          RuleAction.SET_ERROR_MESSAGE,
          this.submissionsConst.completionDateWarning,
        ),
      ]),
    ];
  }

  private toDate(value?: string): number | null {
    if (!value) return null;
    const date = new Date(value);
    return date.setHours(0, 0, 0, 0);
  }

  private createBaseEffect(
    action: RuleAction,
    controlToApplyTo: string,
    value?: string,
  ): FormRuleEffect {
    return {
      action: action,
      affectingType: 'control',
      applyTo: controlToApplyTo,
      value: value,
    };
  }
}
